import { useTours } from 'features/Responder/features/Tours/contexts/TourContext';
import React, { useMemo } from 'react';
import { Button, Card, Form, Stack } from 'react-bootstrap';

const defaultRadius = 75;
const defaultWigglePercent = 20;

/**
 * Sidebar panel to add/edit checkpoints relative to tour legs.
 * Uses TourContext for route data, stops, and checkpoint mutations.
 * @returns {JSX.Element}
 */
export default function CheckpointsPanel() {
  const { routeData, stops, checkpoints, addCheckpoint, updateCheckpoint, removeCheckpoint } =
    useTours();

  const legSummaries = useMemo(() => {
    if (!routeData?.legs || !Array.isArray(routeData.legs)) return [];
    return routeData.legs.map((leg, idx) => ({
      idx,
      minutes: leg.duration ? Number.parseFloat(leg.duration.replace('s', '')) / 60 : null,
    }));
  }, [routeData]);

  /**
   * Add a checkpoint tied to a leg index with default radius/time buffers.
   * @param {number} legIndex
   */
  const handleAdd = (legIndex) => {
    addCheckpoint({
      stopIndex: legIndex,
      radiusMeters: defaultRadius,
      wigglePercent: defaultWigglePercent,
      wiggleAddSeconds: 0,
    });
  };

  return (
    <Card className="flex-grow-1" style={{ maxWidth: 340 }}>
      <Card.Body className="d-flex flex-column gap-3">
        <div>
          <Card.Title>Checkpoints</Card.Title>
          <Card.Text className="text-muted small mb-0">
            Add checkpoints at the end of each leg. Radius and allowed time can be tuned.
          </Card.Text>
        </div>

        {Array.isArray(legSummaries) && legSummaries.length > 0 ? (
          <Stack gap={2}>
            {legSummaries.map((leg) => {
              const stopLabel =
                stops?.[leg.idx]?.address || stops?.[leg.idx]?.label || `Checkpoint ${leg.idx + 1}`;
              return (
                <Card key={leg.idx} className="shadow-sm">
                  <Card.Body className="p-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <div className="fw-semibold">Leg {leg.idx + 1}</div>
                        <div className="small text-muted">
                          To: {stopLabel}
                          {Number.isFinite(leg.minutes) ? ` • ~${Math.round(leg.minutes)} min` : ''}
                        </div>
                      </div>
                      <Button size="sm" variant="primary" onClick={() => handleAdd(leg.idx)}>
                        Add checkpoint
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              );
            })}
          </Stack>
        ) : (
          <div className="text-muted small">Build a route to add checkpoints.</div>
        )}

        {Array.isArray(checkpoints) && checkpoints.length > 0 && (
          <div>
            <div className="fw-semibold mb-2">Active Checkpoints</div>
            <Stack gap={2}>
              {checkpoints.map((cp) => (
                <Card key={cp.id} className="shadow-sm">
                  <Card.Body className="p-3 d-flex flex-column gap-2">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="fw-semibold">Checkpoint (leg {cp.stopIndex + 1})</div>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => removeCheckpoint(cp.id)}
                      >
                        Remove
                      </Button>
                    </div>
                    <Form.Group>
                      <Form.Label className="small text-muted mb-1">Radius (meters)</Form.Label>
                      <Form.Control
                        type="number"
                        size="sm"
                        value={cp.radiusMeters || defaultRadius}
                        onChange={(e) =>
                          updateCheckpoint(cp.id, {
                            radiusMeters: Number(e.target.value),
                          })
                        }
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label className="small text-muted mb-1">Wiggle room %</Form.Label>
                      <Form.Control
                        type="number"
                        size="sm"
                        value={cp.wigglePercent ?? defaultWigglePercent}
                        onChange={(e) =>
                          updateCheckpoint(cp.id, {
                            wigglePercent: Number(e.target.value),
                          })
                        }
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label className="small text-muted mb-1">Extra seconds</Form.Label>
                      <Form.Control
                        type="number"
                        size="sm"
                        value={cp.wiggleAddSeconds ?? 0}
                        onChange={(e) =>
                          updateCheckpoint(cp.id, {
                            wiggleAddSeconds: Number(e.target.value),
                          })
                        }
                      />
                    </Form.Group>
                  </Card.Body>
                </Card>
              ))}
            </Stack>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
